import type { CaseListItem } from "@/domain/case/case";
import type { PendingApproval } from "@/features/approvals/use-approvals";

/** 通知の種類。人に伝える出来事の分類（Messages とは別レイヤー）。 */
export type NotificationKind = "approvalRequested" | "returned" | "completed";

export type NotificationItem = {
  /** 同じ出来事で安定する id（既読管理・key に使う）。 */
  readonly id: string;
  readonly kind: NotificationKind;
  readonly caseId: number;
  readonly caseNumber: string;
  /** 案件詳細を開くのに必要（API のパスに使う）。 */
  readonly processModelId: string;
  readonly title: string;
  /** 承認依頼のときの起票者。 */
  readonly applicant: string | null;
  readonly occurredAt: Date | null;
  readonly unread: boolean;
};

export type DeriveNotificationsInput = {
  readonly approvals: readonly PendingApproval[];
  readonly myCases: readonly CaseListItem[];
  /** これより後の出来事を未読にする。無ければすべて未読。 */
  readonly lastSeenAt?: Date | null;
  /**
   * 個別に既読にした通知（id → その時点の発生時刻）。
   *
   * id は使い回されるので（同じ案件が再度差し戻されると id は同じ）、発生時刻が
   * ここより新しければ**再び未読**として扱う。
   */
  readonly readAt?: ReadonlyMap<string, number>;
};

/**
 * 通知は専用テーブルを持たず、既存データから導出する（issue #29 の確定仕様・MVP）。
 *
 * - 承認依頼 … 自分に回っている承認待ちタスク
 * - 差し戻し / 完了 … 自分が起票した案件の状態
 *
 * 既読は「最終閲覧時刻より後か」で判定する（サーバに既読状態を持たない）。
 */
export function deriveNotifications(
  input: DeriveNotificationsInput,
): readonly NotificationItem[] {
  const seen = input.lastSeenAt?.getTime() ?? null;
  const readAt = input.readAt ?? new Map<string, number>();

  const fromApprovals = input.approvals.map<NotificationItem>((approval) => ({
    id: `approval:${approval.taskGuid}`,
    kind: "approvalRequested",
    caseId: approval.caseId,
    caseNumber: approval.caseNumber,
    processModelId: approval.processModelId,
    title: approval.processModelDisplayName,
    applicant: approval.applicant,
    occurredAt: approval.requestedAt,
    unread: isUnread(
      `approval:${approval.taskGuid}`,
      approval.requestedAt,
      seen,
      readAt,
    ),
  }));

  const fromCases = input.myCases
    .filter((item) => item.status === "returned" || item.status === "completed")
    .map<NotificationItem>((item) => ({
      id: `case:${item.caseId}:${item.status}`,
      kind: item.status === "returned" ? "returned" : "completed",
      caseId: item.caseId,
      caseNumber: item.caseNumber,
      processModelId: item.processModelId,
      // 通知の見出しは件名が無ければ種別で代替する（空にしない）
      title: item.title ?? item.processModelDisplayName,
      applicant: null,
      occurredAt: item.updatedAt ?? item.completedAt,
      unread: isUnread(
        `case:${item.caseId}:${item.status}`,
        item.updatedAt ?? item.completedAt,
        seen,
        readAt,
      ),
    }));

  return [...fromApprovals, ...fromCases].sort(
    (a, b) => (b.occurredAt?.getTime() ?? 0) - (a.occurredAt?.getTime() ?? 0),
  );
}

/**
 * 未読かどうか。「すべて既読にした時刻」と「この通知を個別に既読にした時点の発生時刻」の
 * **新しい方**を基準にする（どちらか一方でも既読と言っていれば既読）。
 */
function isUnread(
  id: string,
  occurredAt: Date | null,
  seen: number | null,
  readAt: ReadonlyMap<string, number>,
): boolean {
  const threshold = Math.max(
    seen ?? Number.NEGATIVE_INFINITY,
    readAt.get(id) ?? Number.NEGATIVE_INFINITY,
  );

  return (occurredAt?.getTime() ?? 0) > threshold;
}
